import { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

export type TabKey = 'live' | 'upcoming' | 'results'

export type TabDef = {
    key: TabKey
    icon: IconDefinition
    label: string
    count: number
    /** One short fact about this bucket — the pool at stake, when the next one opens. */
    note?: ReactNode
}

/**
 * Live / Upcoming / Results, as one segmented control.
 *
 * The page used to stack all three as separate sections, which cost 5.1 screens of scroll on a
 * phone and — on a wide screen with one race in each bucket — two half-width cards sitting
 * vertically beside a permanently empty column. Slicing tournaments by time is the one axis this
 * page has, so it belongs in a control rather than in the scroll position.
 *
 * The counts and notes are why the hero no longer carries a summary strip: "1 live, $1,650 on the
 * line, next one in 10d" is the same three numbers, read off the thing you are about to click.
 */
export const TournamentTabs = ({
    tabs,
    selected,
    onSelect,
    className,
}: {
    tabs: TabDef[]
    selected: TabKey
    onSelect: (key: TabKey) => void
    className?: string
}) => (
    // Three across at every width. The labels are one word each, so unlike the Inventory page's
    // five categories they do not need to wrap on a 375px screen.
    <div role='tablist' className={twMerge('grid grid-cols-3 gap-1.5 sm:gap-2 md:flex md:flex-row', className)}>
        {tabs.map((tab) => (
            <Tab key={tab.key} tab={tab} selected={tab.key === selected} onSelect={() => onSelect(tab.key)} />
        ))}
    </div>
)

const Tab = ({ tab, selected, onSelect }: { tab: TabDef; selected: boolean; onSelect: () => void }) => (
    <button
        role='tab'
        aria-selected={selected}
        onClick={onSelect}
        className={twMerge(
            'group flex min-h-[2.75rem] min-w-0 flex-col items-start gap-0.5 border-b-2 px-2.5 py-2 text-left',
            'transition-colors sm:px-4 sm:py-2.5 md:min-w-[10rem]',
            selected
                ? 'border-b-[#C91FFF] bg-[linear-gradient(90deg,rgba(3,206,255,0.2)_0%,rgba(201,31,255,0.2)_100%)]'
                : 'border-b-transparent bg-white/5 hover:bg-white/10'
        )}
    >
        {/* Stacked on a phone. Side by side, a third of 375px leaves 62px for the label and
            "UPCOMING" wants 70 — so the part that got cut was the part that names the tab. The
            Inventory categories hit this exact wall; same answer. */}
        <span className='flex w-full min-w-0 flex-col items-start gap-0.5 leading-none sm:flex-row sm:items-baseline sm:gap-1.5'>
            <span className='flex items-center gap-1.5'>
                <FontAwesomeIcon
                    icon={tab.icon}
                    className={twMerge(
                        'shrink-0 text-2xs transition-colors',
                        selected ? 'text-[#03CEFF]' : 'text-white/30 group-hover:text-white/60'
                    )}
                />
                <span className='text-base font-black leading-none tabular-nums'>{tab.count}</span>
            </span>
            <small className='w-full truncate text-2xs font-bold uppercase tracking-[0.12em] sm:w-auto sm:text-xs'>
                {tab.label}
            </small>
        </span>
        {/* Reserved even when empty, so selecting a tab never reflows the row beneath it. */}
        <small className='w-full truncate text-2xs font-bold uppercase leading-tight tracking-[0.1em] text-white/45'>
            {tab.note ?? ' '}
        </small>
    </button>
)
